import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { securePost } from "../../config/api";
import ConfigContext from "../../Shared/ConfigContext/ConfigContext";
import { setSelectedProduct } from "../../Shared/ConfigContext/Actions";
import { formatDateTime } from "../../utils/format";

import SnackbarNotification from "../../components/SnackbarNotification";
import UserContext from "../../Shared/UserContext";

import CloseIcon from "../../icons/x.svg";
import CheckIcon from "../../icons/checkG.svg";
import XIcon from "../../icons/xB.svg";
import noImage from "../../icons/noImageIcon.svg";

const TransferRequestDetails = ({ request, onClose, onValidated, onSave, onValidate }) => {
  const { state, dispatch } = useContext(ConfigContext);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [locallySelectedProducts, setLocallySelectedProducts] = useState(state.selectedProducts);
  const { state: { role } } = useContext(UserContext);

  const { t } = useTranslation();

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const handleToggleSelect = (productId) => {
    setLocallySelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleEnregistrer = () => {
    dispatch(setSelectedProduct(locallySelectedProducts));
    onSave(t('confirmation.saveRequestDetails'));  
    onClose();
  };

  const handleValidate = async () => {
  if (!locallySelectedProducts.length || locallySelectedProducts.length !== request.details.length) {
    showSnackbar(
      t('errors.selectAllBeforeValidate'),
      'error',
      true
    );
    return;
  }
    const requestBody = {
      draftId: request.id,
      products: request.details
        .filter(product => locallySelectedProducts.includes(product.id))
        .map(product => ({
          productId: product.product.id,
          quantity: product.quantity,
        })),
    };


    try {
      const response = await securePost("/repos/transfer/draft/validate", requestBody, showSnackbar);

      onValidated(request.id);  

      onValidate(t('confirmation.validateRequestDetails'));  

      onClose()


    } catch (error) {
      console.error("Error during validation:", error);
    }
  };


  const allProductsSelected = locallySelectedProducts.length === request.details.length;

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "50px";
    e.target.style.height = "50px";
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
      <div className="relative bg-[#F9FAFC] h-[750px] rounded-lg p-8 shadow-lg flex flex-col w-full max-w-screen-xl">

        {/* Close Button */}
        <button
          className="absolute top-4 right-4 w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-full shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
          onClick={onClose}
        >
          <img src={CloseIcon} alt="Close" />
          </button>

        {/* Title */}
        <div className="mb-4">
          <h1 className="text-[24px] font-semibold text-black">
            {t(`titles.request_details`)}
          </h1>
        </div>

        {/* Order Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4 py-4 pl-6 bg-white rounded-lg shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
          <div className="flex flex-col">
            <p className="text-[#707070] text-[14px] font-semibold">
              {t(`rows.creationDate`)}
            </p>
            <p className="text-[#38485C] text-[20px] font-semibold">
              {formatDateTime(request.createdAt)}
            </p>
          </div>
          <div className="flex flex-col">
            <p className="text-[#707070] text-[14px] font-semibold">
              {t(`rows.commercial`)}
            </p>
            <p className="text-[#38485C] text-[20px] font-semibold">
              {request.user.firstName} {request.user.lastName}
            </p>
          </div>
          <div className="flex flex-col">
            <p className="text-[#707070] text-[14px] font-semibold">SOURCE</p>
            <p className="text-[#38485C] text-[20px] font-semibold">
              {request.sourceRepo?.name}
            </p>
          </div>
          <div className="flex flex-col">
            <p className="text-[#707070] text-[14px] font-semibold">
              DESTINATION
            </p>
            <p className="text-[#38485C] text-[20px] font-semibold">
              {request.destRepo?.name}
            </p>
          </div>
        </div>

        <p className="text-[#EF476F] text-[12px] font-medium mb-2">{t(`placeholder.transferWarning`)}</p>


        {/* Scrollable Product List */}
        <div className="flex-1 overflow-y-auto mb-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-[#ACAFC6] text-[16px] font-medium">{t(`fields_types.list`)}</p>
            <p className="text-[#ACAFC6] text-[16px] font-medium">
            {locallySelectedProducts.length}/{request.details.length} {t(`placeholder.selectedElements`)}            </p>
          </div>

          {/* Header Row */}
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_0.5fr] text-left font-semibold text-[#707070] text-[14px] px-6 mb-2 items-center">
            <div className="text-left">{t(`rows.product`)}</div>
            <div className="text-center">{t(`rows.currentStock`)}</div>
            <div className="text-center"></div>
            <div className="text-center">{t(`rows.quantity`)}</div>
            <div className="text-center">{t(`rows.newStock`)}</div>
            <div className="text-center"></div>
          </div>

          {/* Data Rows */}
          {request.details.map((productDetail) => {
            const { id, product, quantity } = productDetail;

            const sourceStock = product.availability.find(
              (stock) => stock.repoId === request.sourceRepo?.id
            )?.quantity || 0;

            const destinationStock = product.availability.find(
              (stock) => stock.repoId === request.destRepo?.id
            )?.quantity || 0;

            const newSourceStock = sourceStock - quantity;
            const newDestinationStock = destinationStock + quantity;

            const isSelected = locallySelectedProducts.includes(id);


            return (
              <div
                key={id}
                className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_0.5fr] items-center bg-white py-[8px] px-6 rounded-lg shadow-[0px_6px_26px_rgba(98,107,125,0.07)] mb-2 ${isSelected ? "border-2 border-[#06D6A0]" : ""
                  }`}
              >
                {/* Product Information */}
                <div className="flex items-center gap-2">
                  <div className="w-[60px] h-[75px] flex items-center justify-center flex-shrink-0 rounded-md overflow-hidden shadow-[0px_4px_16px_#626B7D12]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain"
                      onError={handleImageError}
                    />
                  </div>
                  <div>
                    <p className="text-[#043F88] text-[12px] font-semibold">
                      {product.reference}
                    </p>
                    <p className="text-[#6B78F8] text-[16px] font-semibold">
                      {product.name}
                    </p>
                  </div>
                </div>

                {/* Stock Information */}

                <div className="flex flex-col items-center">
                  <p className="text-[#6B78F8] text-[26px] font-semibold">{sourceStock}</p>
                  <p className="text-[#043F88] text-[12px] font-medium">Source</p>
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-[#6B78F8] text-[26px] font-semibold">{destinationStock}</p>
                  <p className="text-[#043F88] text-[12px] font-medium">Destination</p>
                </div>
                <div className="text-center">
                  <p className="text-[#38485C] text-[32px] font-semibold">{quantity}</p>
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-[#6B78F8] text-[26px] font-semibold">{newSourceStock}</p>
                  <p className="text-[#043F88] text-[12px] font-medium">Source</p>
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-[#6B78F8] text-[26px] font-semibold">{newDestinationStock}</p>
                  <p className="text-[#043F88] text-[12px] font-medium">Destination</p>
                </div>
    

                {/* Action Button */}
                {(role === "ROLE_ADMIN") && (
                <div className="flex justify-center">
                  <button
                    onClick={() => handleToggleSelect(id)}
                    className="bg-white shadow-[0px_6px_26px_#626B7D12] border border-[#F9FAFC] rounded-lg flex items-center justify-center w-10 h-10"
                  >
                    <img src={isSelected ? XIcon : CheckIcon} alt="Toggle" className="w-6 h-6" />
                    </button>
                </div>
                )}
              </div>
            );
          })}
        </div>


        {/* Action Buttons */}
        {(role === "ROLE_ADMIN") && (
        <div className="flex justify-end gap-4">
          <button
            onClick={handleEnregistrer}
            className="bg-[#6B78F8] text-white px-6 py-2 rounded-lg font-semibold shadow-[0px_6px_26px_#626B7D12] hover:bg-[#5A67D8]"
          >
            {t(`actions.save`)}
          </button>
 <button
   onClick={handleValidate}
   className={`px-6 py-2 rounded-lg font-semibold ${
     allProductsSelected
       ? "bg-[#0AD69F] text-white hover:bg-[#33D9A2]"
       : "bg-[#0AD69F1A] text-[#0AD69F] border border-[#0AD69F] hover:bg-[#0AD69F1A]"
   }`}
 >
            {t(`actions.validate`)}
          </button>
        </div>
        )}
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default TransferRequestDetails;
